import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useGetCurrentUser from "../../../hooks/useGetCurrentUser";
import {
  declineOfferedSeat,
  joinWorkshopWaitlist,
  leaveWorkshopWaitlist,
  registerForWorkshop,
  unRegisterFromWorkshop,
} from "../../../state/workshopSlice";
import { DateTime } from "luxon";
import style from "./RegistrationControls.module.css";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import BeenhereIcon from "@mui/icons-material/Beenhere";
import GroupIcon from "@mui/icons-material/Group";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";

const RegistrationControls = ({ workshop }) => {
  const dispatch = useDispatch();

  const { userWorkshops } = useSelector((state) => state.workshops);
  const { status } = useSelector((state) => state.workshops);
  //state for add to calendar confirm dialog visibility
  const [dialogOpen, setDialogOpen] = useState(false);
  //add to calendar toggle
  const [addToCalendar, setAddToCalendar] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);

  //is in registration period
  const [inRegistrationPeriod, setInRegistrationPeriod] = useState(false);
  //is the workshop over
  const [workshopOver, setWorkshopOver] = useState(false);

  //set to true if current user is offered a seat from the waitlist
  const [isOfferedSeat, setIsOfferedSeat] = useState(false);
  //if current user is on waitlist for current workshop waitlist details will be held here
  const [waitlistInfo, setWaitlistInfo] = useState(null);
  //is the workshop full
  const [isFull, setIsFull] = useState(false);

  //registration status message for this workshop
  const [regStatus, setRegStatus] = useState({ type: "success" });
  const user = useGetCurrentUser();

  //register for workshop
  const register = (addEvent) => {
    dispatch(
      registerForWorkshop(
        workshop.workshopId,
        user.mail,
        addEvent,
        isOfferedSeat
      )
    );
    setDialogOpen(false);
  };

  useEffect(() => {
    //get some dates for comparisons
    const today = new Date().setHours(0, 0, 0, 0);
    const registrationStart = new Date(
      workshop.registrationStartDate + "T00:00:00"
    );
    const registrationEnd = new Date(
      workshop.registrationEndDate + "T00:00:00"
    );
    const workshopEnd = Date.parse(workshop.endDatetime);

    //if today is after the end of the workshop the workshop is over
    setWorkshopOver(today >= workshopEnd);

    //if today is within the registration start and end dates registration is open
    setInRegistrationPeriod(
      today >= registrationStart && today <= registrationEnd
    );

    setIsRegistered(
      userWorkshops.registrations.some(
        (w) => w.workshopId === workshop.workshopId
      )
    );

    const userWaitlistInfo = userWorkshops.waitlists.find(
      (w) => w.workshopId === workshop.workshopId
    );

    //user is on waitlist if userWaitlistInfo has a value
    setIsOnWaitlist(!!userWaitlistInfo);

    setWaitlistInfo(userWaitlistInfo);

    setIsOfferedSeat(
      !userWaitlistInfo?.offerExpiredDatetime &&
        !!userWaitlistInfo?.offeredSeatDatetime
    );

    setIsFull(workshop.seats.numberRegistered >= workshop.seats.capacity);
  }, [workshop, userWorkshops, user]);

  useEffect(() => {}, [status]);

  useEffect(() => {
    if (workshopOver) {
      setRegStatus({
        message: "This workshop is over",
        type: "error",
      });
    } else if (isRegistered)
      setRegStatus({
        message: "You are registered for this workshop",
        type: "success",
      });
    else if (isOfferedSeat)
      setRegStatus({
        message: "A seat is being held for you!",
        type: "warning",
      });
    else if (isOnWaitlist)
      setRegStatus({
        message: "You are on the waitlist for this workshop",
        type: "info",
      });
    else if (isFull && !workshop.disableWaitlist)
      setRegStatus({
        message: "All seats are reserved but you can join the waitlist",
        type: "warning",
      });
    else if (isFull) {
      setRegStatus({
        message: "The workshop is full and no waitlist is available",
        type: "warning",
      });
    }
  }, [isFull, isOnWaitlist, isRegistered, isOfferedSeat, workshopOver]);

  return (
    <>
      {(workshopOver || isRegistered || isOnWaitlist || isFull) && (
        <Alert
          iconMapping={{
            success: <BeenhereIcon fontSize="large" />,
            info: <AccessTimeIcon fontSize="large" />,
            warning: <GroupIcon fontSize="large" />,
            error: <BeachAccessIcon fontSize="large" />,
          }}
          severity={regStatus.type}
          variant="filled"
          sx={{ marginBottom: "1rem", alignItems: "center" }}
        >
          <Typography variant="h4">{regStatus.message}</Typography>
          {isOfferedSeat && (
            <Typography>
              The seat will be held until{" "}
              <strong>
                <u>
                  {DateTime.fromISO(waitlistInfo.offeredSeatDatetime)
                    .plus({
                      hours: 24,
                    })
                    .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}
                </u>
              </strong>
            </Typography>
          )}
        </Alert>
      )}
      {!workshopOver && (
        <Box className={style.outerBox}>
          <Box className={style.seats} sx={{ bgcolor: "background.main" }}>
            {inRegistrationPeriod ? (
              <>
                <Box>
                  <EventSeatIcon fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h5">
                    {workshop.seats.emptySeats} available seats
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography variant="h4">Registration is closed</Typography>
            )}
          </Box>

          {!isFull && !isRegistered && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                justifyContent: "flex-end",
              }}
            >
              <Tooltip
                title={
                  <>
                    Do you want to add this workshop to your calendar on
                    registration?<br></br> Currently set to{" "}
                    <strong>{addToCalendar ? "Yes" : "No"}</strong>
                  </>
                }
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={addToCalendar}
                        onChange={() => setAddToCalendar(!addToCalendar)}
                        disabled={!inRegistrationPeriod}
                      />
                    }
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <CalendarMonthIcon /> Add to my calendar
                      </Box>
                    }
                    labelPlacement="start"
                  />
                </FormGroup>
              </Tooltip>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircleIcon />}
                color="secondary"
                onClick={() => register(addToCalendar)}
                className={style.button}
                disabled={!inRegistrationPeriod}
              >
                Register
              </Button>
            </Box>
          )}
          {isRegistered && (
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<DoNotDisturbAltIcon />}
              onClick={() =>
                dispatch(unRegisterFromWorkshop(workshop.workshopId, user.mail))
              }
              className={style.button}
            >
              Un-Register
            </Button>
          )}
          {isFull &&
            !isRegistered &&
            !isOnWaitlist &&
            !workshop.disableWaitlist && (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<AccessTimeIcon />}
                onClick={() =>
                  dispatch(joinWorkshopWaitlist(workshop.workshopId, user.mail))
                }
                className={style.button}
              >
                Join Waitlist
              </Button>
            )}
          {isOnWaitlist && !isOfferedSeat && (
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<DoNotDisturbAltIcon />}
              onClick={() =>
                dispatch(leaveWorkshopWaitlist(workshop.workshopId, user.mail))
              }
              className={style.button}
            >
              Leave Waitlist
            </Button>
          )}
          {isOfferedSeat && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "0.25rem",
              }}
            >
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Tooltip
                  title={
                    <>
                      Do you want to add this workshop to your calendar on
                      registration?<br></br> Currently set to{" "}
                      <strong>{addToCalendar ? "Yes" : "No"}</strong>
                    </>
                  }
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={addToCalendar}
                          onChange={() => setAddToCalendar(!addToCalendar)}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                          }}
                        >
                          <CalendarMonthIcon /> Add to my calendar
                        </Box>
                      }
                      labelPlacement="start"
                    />
                  </FormGroup>
                </Tooltip>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<CheckIcon />}
                  onClick={() => register(addToCalendar)}
                  className={style.button}
                >
                  Accept Offered Seat
                </Button>
              </Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<DoNotDisturbAltIcon />}
                onClick={() =>
                  dispatch(declineOfferedSeat(workshop.workshopId, user.mail))
                }
                className={style.button}
              >
                Decline
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default RegistrationControls;
